// src/context/SettingsContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { axiosInstance } from "../axiosInstance";
import { AuthContext } from "./AuthContext";

export const SettingsContext = createContext();

// Helper function to local preferences from localStorage

function loadLocalPreferences() {
  // Attempt to parse from localStorage, otherwise fallback defaults
  try {
    const stored = localStorage.getItem("m_prefs");
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (err) {
    console.error("SettingsContext: Error parsing localStorage", err);
  }
  // Return fallback defaults if none found
  return {
    darkMode: false,
    typewriterEffect: true,
    chatTextSize: "medium",
    chefPersona: "a friendly cooking instructor",
    dietaryRestrictions: ["None"],
    measurementUnits: "international",
    language: "en",
    // notifications: { email: false, push: false },
    foodAllergies: ["None"],
  };
}

export function SettingsProvider({ children }) {
  const { accessToken } = useContext(AuthContext);

  // ------------------------------
  // Existing user preference states
  // ------------------------------

  const [darkMode, setDarkMode] = useState(false);
  const [chefPersona, setChefPersona] = useState(
    "a friendly cooking instructor"
  );
  const [dietaryRestrictions, setDietaryRestrictions] = useState(["None"]);
  const [measurementUnits, setMeasurementUnits] = useState("international");
  const [language, setLanguage] = useState("en");
  const [notifications, setNotifications] = useState({
    email: false,
    push: false,
  });
  const [foodAllergies, setFoodAllergies] = useState(["None"]);
  const [typewriterEffect, setTypewriterEffect] = useState(false);
  const [chatTextSize, setChatTextSize] = useState("medium");

  const [rememberPreferences, setRememberPreferences] = useState(true);

  // ------------------------------
  // New: user profile data
  // ------------------------------
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState("");
  const [birthday, setBirthday] = useState(null); // e.g. { month: 3, day: 14 } or null

  console.log("rememberPreferences = ", rememberPreferences);
  // ------------------------------
  // 1. On mount, if user is NOT logged in, load from localStorage
  //    If user is logged in, we'll fetch from server (below).
  // ------------------------------
  useEffect(() => {
    if (!accessToken) {
      // no token => read from localStorage
      const local = loadLocalPreferences();
      setDarkMode(!!local.darkMode);
      setTypewriterEffect(!!local.typewriterEffect);
      setChatTextSize(local.chatTextSize || "medium");
      setChefPersona(local.chefPersona || "a friendly cooking instructor");
      setDietaryRestrictions(local.dietaryRestrictions || ["None"]);
      setMeasurementUnits(local.measurementUnits || "international");
      setLanguage(local.language || "en");
      setNotifications(local.notifications || { email: false, push: false });
      setFoodAllergies(local.foodAllergies || ["None"]);

      // For profile data, if user is not logged in, we have no server data
      setFirstName("");
      setLastName("");
      setGender("");
      setBirthday(null);
    }
  }, [accessToken]);

  // 2. On mount or if accessToken changes, fetch user settings & profile
  useEffect(() => {
    async function fetchData() {
      if (!accessToken) return;

      try {
        // (1) Fetch user preferences from /api/user/settings
        const resSettings = await axiosInstance.get("/api/user/settings", {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const s = resSettings.data || {};

        // Merge preference fields
        if (typeof s.rememberPreferences === "boolean") setRememberPreferences(s.rememberPreferences);
        if (typeof s.darkMode === "boolean") setDarkMode(s.darkMode);
        if (s.chefPersona) setChefPersona(s.chefPersona);
        if (Array.isArray(s.dietaryRestrictions)) {
          setDietaryRestrictions(s.dietaryRestrictions);
        }
        if (s.measurementUnits) setMeasurementUnits(s.measurementUnits);
        if (s.language) setLanguage(s.language);
        if (s.notifications) setNotifications(s.notifications);
        if (s.typewriterEffect) setTypewriterEffect(s.typewriterEffect);
        if (s.chatTextSize) setChatTextSize(s.chatTextSize);
        if (Array.isArray(s.foodAllergies)) {
          setFoodAllergies(s.foodAllergies);
        }

        // (2) Fetch user profile from /api/user/profile
        const resProfile = await axiosInstance.get("/api/user/profile", {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const p = resProfile.data || {};
        if (p.firstName) setFirstName(p.firstName);
        if (p.lastName) setLastName(p.lastName);
        if (p.gender) setGender(p.gender);
        if (p.birthday) setBirthday(p.birthday); // e.g. { month: 3, day: 14 }
      } catch (error) {
        console.error("SettingsContext: Failed to fetch data", error);
      }
    }
    fetchData();
  }, [accessToken]);

  // ------------------------------
  // 3. Persist to localStorage if user is NOT logged in
  //    whenever preferences change
  // ------------------------------
  useEffect(() => {
    if (accessToken && rememberPreferences) {
      // Build object
      const localPrefs = {
        darkMode,
        typewriterEffect,
        chatTextSize,
        chefPersona,
        dietaryRestrictions,
        measurementUnits,
        language,
        // notifications,
        foodAllergies,
      };
      console.log("Local preferences: ", localPrefs);
      localStorage.setItem("m_prefs", JSON.stringify(localPrefs));
    }
  }, [
    accessToken,
    rememberPreferences,
    darkMode,
    typewriterEffect,
    chatTextSize,
    chefPersona,
    dietaryRestrictions,
    measurementUnits,
    language,
    notifications,
    foodAllergies,
  ]);

  // ---------------
  // Updating PREFERENCES
  // ---------------
  async function updateSettings(newSettings) {
    if (!accessToken) {
      console.warn("Cannot update settings, user not logged in");
      return;
    }
    try {
      await axiosInstance.put("/api/user/settings", newSettings, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      // Merge local states (preferences only)
      if ("rememberPreferences" in newSettings) setRememberPreferences(!!newSettings.rememberPreferences);
      if ("darkMode" in newSettings) setDarkMode(!!newSettings.darkMode);
      if ("chefPersona" in newSettings) setChefPersona(newSettings.chefPersona);
      if ("dietaryRestrictions" in newSettings)
        setDietaryRestrictions(newSettings.dietaryRestrictions);
      if ("measurementUnits" in newSettings)
        setMeasurementUnits(newSettings.measurementUnits);
      if ("language" in newSettings) setLanguage(newSettings.language);
      if ("notifications" in newSettings)
        setNotifications(newSettings.notifications);
      if ("foodAllergies" in newSettings)
        setFoodAllergies(newSettings.foodAllergies);
      if ("typewriterEffect" in newSettings) {
        setTypewriterEffect(!!newSettings.typewriterEffect);
      }
      if ("chatTextSize" in newSettings) {
        setChatTextSize(newSettings.chatTextSize);
      }
    } catch (error) {
      console.error("SettingsContext: Failed to update preferences", error);
    }
  }

  // ---------------
  // Updating PROFILE
  // ---------------
  async function updateProfile(profileData) {
    // e.g. { firstName, lastName, gender, birthday: { month, day } }
    if (!accessToken) {
      console.warn("Cannot update profile, user not logged in");
      return;
    }
    try {
      await axiosInstance.put("/api/user/profile", profileData, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      // Merge local states (profile fields)
      if ("firstName" in profileData) setFirstName(profileData.firstName);
      if ("lastName" in profileData) setLastName(profileData.lastName);
      if ("gender" in profileData) setGender(profileData.gender);
      if ("birthday" in profileData) setBirthday(profileData.birthday);
    } catch (error) {
      console.error("SettingsContext: Failed to update profile", error);
    }
  }

  return (
    <SettingsContext.Provider
      value={{
        // Preferences
        rememberPreferences,
        darkMode,
        typewriterEffect,
        chatTextSize,
        chefPersona,
        dietaryRestrictions,
        measurementUnits,
        language,
        notifications,
        foodAllergies,
        updateSettings,

        // Profile
        firstName,
        lastName,
        gender,
        birthday,
        updateProfile,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}
